import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import Icon from './Icon';
import BrandIcon from './BrandIcon';
import { useOrgBrand } from '../context/OrgBrandContext';
import { useModal } from '../context/ModalContext';
import { useSortableTable } from '../hooks/useSortableTable';
import '../styles/Recordings.css';
import CustomSelect from './CustomSelect';

const Recordings = ({ user, activeAgency }) => {
  const modal = useModal();
  const { selectedOrganization, selectedBrand, loading: orgBrandLoading } = useOrgBrand();

  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [fetchingRecordings, setFetchingRecordings] = useState({});
  const [transcribingRecordings, setTranscribingRecordings] = useState({});
  const [selectedFormats, setSelectedFormats] = useState({});
  const [transcriptModal, setTranscriptModal] = useState({ open: false, transcript: null, title: '' });
  const [generatingIdeas, setGeneratingIdeas] = useState({});
  const [isPolling, setIsPolling] = useState(false);

  // Assignment state
  const [accessibleOrgs, setAccessibleOrgs] = useState([]);
  const [accessibleBrands, setAccessibleBrands] = useState([]);
  const [assigningRecording, setAssigningRecording] = useState({});
  const [editingOrg, setEditingOrg] = useState({});
  const [editingBrand, setEditingBrand] = useState({});

  const isSuperAdmin = user?.is_super_admin;
  const isAdmin = user?.is_admin;

  // Extract the actual IDs we use for filtering
  const orgId = selectedOrganization?.id;
  const brandId = selectedBrand?.id;

  // Use refs to track filter status and org/brand selection in polling callback
  const filterStatusRef = useRef(filterStatus);
  const selectedOrgRef = useRef(selectedOrganization);
  const selectedBrandRef = useRef(selectedBrand);
  filterStatusRef.current = filterStatus;
  selectedOrgRef.current = selectedOrganization;
  selectedBrandRef.current = selectedBrand;

  // Fetch recordings with org/brand filtering
  const fetchRecordings = useCallback(async () => {
    try {
      setLoading(true);

      const params = {
        status: filterStatus !== 'all' ? filterStatus : undefined,
        organization_id: orgId,
        brand_id: brandId
      };

      const response = await axios.get('/api/meetings/recordings', {
        params,
        withCredentials: true
      });

      setRecordings(response.data.recordings || []);
    } catch (err) {
      console.error('Error fetching recordings:', err);
      if (err.response?.status === 403) {
        modal.error("You haven't been assigned as an operator on this brand yet, so its recordings aren't visible to you.");
      } else {
        modal.error('Failed to load recordings. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [filterStatus, orgId, brandId]);

  useEffect(() => {
    // Wait for the org/brand context to finish restoring the saved selection
    // before fetching - otherwise this fires with no org/brand filter and
    // briefly (or permanently, if the orgs/brands never settle) shows every
    // recording across every org/brand instead of just the selected one.
    if (orgBrandLoading) {
      return;
    }
    // For super admins, wait until activeAgency is set before fetching
    // For admins/operators, fetch immediately (they can only see their own agency)
    if (isSuperAdmin && !activeAgency) {
      // Don't fetch yet - waiting for agency to be selected
      setLoading(false);
      return;
    }
    fetchRecordings();
  }, [filterStatus, activeAgency, isSuperAdmin, orgBrandLoading, fetchRecordings]);

  // Fetch accessible organizations and brands for assignment dropdown
  // Re-fetch when agency changes since orgs/brands are agency-scoped
  useEffect(() => {
    const fetchAccessibleEntities = async () => {
      // For super admins, wait until activeAgency is set before fetching
      // For admins/operators, fetch immediately (they can only see their own agency)
      if (isSuperAdmin && !activeAgency) {
        return;
      }

      try {
        const [orgsResponse, brandsResponse] = await Promise.all([
          axios.get('/api/user/organizations', { withCredentials: true }),
          axios.get('/api/brands/accessible', { withCredentials: true })
        ]);

        setAccessibleOrgs(orgsResponse.data.organizations || []);

        // Sort brands alphabetically by name
        const brands = brandsResponse.data.brands || [];
        const sortedBrands = brands.sort((a, b) => {
          const nameA = (a.brand_name || a.name || '').toLowerCase();
          const nameB = (b.brand_name || b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });

        setAccessibleBrands(sortedBrands);
      } catch (err) {
        console.error('Error fetching accessible entities:', err);
      }
    };

    fetchAccessibleEntities();
  }, [activeAgency, isSuperAdmin]);

  // Memoized silent fetch function
  const fetchRecordingsSilent = useCallback(async () => {
    try {
      const params = {
        status: filterStatusRef.current !== 'all' ? filterStatusRef.current : undefined,
        organization_id: selectedOrgRef.current?.id,
        brand_id: selectedBrandRef.current?.id
      };

      const response = await axios.get('/api/meetings/recordings', {
        params,
        withCredentials: true
      });

      setRecordings(response.data.recordings || []);
    } catch (err) {
      console.error('Silent refresh error:', err);
    }
  }, []);

  // Auto-poll for pending recordings every 30 seconds
  // Only poll if meetings have ended recently (within 1 hour) and still pending/processing
  useEffect(() => {
    const now = new Date();
    const POLL_WINDOW_MS = 60 * 60 * 1000; // 1 hour after scheduled end

    const hasPendingOrProcessing = recordings.some(r => {
      const isPendingOrProcessing = r.recording_status === 'pending' ||
                                     r.recording_status === 'processing' ||
                                     r.recording_status === 'not_started' ||
                                     r.recording_status === 'waiting_for_audio';
      if (!isPendingOrProcessing) return false;

      // Only poll if meeting has ended and it's been less than 1 hour since scheduled end
      if (r.scheduled_end) {
        const endTime = new Date(r.scheduled_end);
        // Don't poll if meeting hasn't ended yet (future meeting)
        if (now < endTime) return false;
        // Don't poll if more than 1 hour has passed since meeting ended
        if (now > new Date(endTime.getTime() + POLL_WINDOW_MS)) return false;
      }

      return true;
    });

    setIsPolling(hasPendingOrProcessing);

    if (!hasPendingOrProcessing) return;

    const interval = setInterval(() => {
      fetchRecordingsSilent();
    }, 30000); // Poll every 30 seconds

    return () => clearInterval(interval);
  }, [recordings, fetchRecordingsSilent]);

  const handleRefresh = () => {
    fetchRecordings();
  };

  const handleDelete = async (meetingId) => {
    const result = await modal.confirm('Are you sure you want to delete this recording? This action cannot be undone.', 'Delete Recording');
    if (!result) {
      return;
    }

    try {
      await axios.delete(`/api/meetings/${meetingId}`, {
        withCredentials: true
      });

      modal.success('Recording deleted successfully!');
      fetchRecordings();
    } catch (err) {
      console.error('Error deleting recording:', err);
      modal.error(err.response?.data?.error || 'Failed to delete recording');
    }
  };

  const handleFetchRecordings = async (meetingId) => {
    // Check if this is a refetch (for super admins)
    const recording = recordings.find(r => r.meeting_id === meetingId);
    if (isSuperAdmin && recording?.recording_status === 'completed') {
      const result = await modal.confirm('This meeting already has recordings.\nAre you sure you want to refetch? This will replace all existing recordings.', 'Refetch Recordings');
      if (!result) {
        return;
      }
    }

    setFetchingRecordings(prev => ({ ...prev, [meetingId]: true }));

    try {
      const response = await axios.post(
        `/api/meetings/${meetingId}/fetch-squadcast-recordings`,
        {},
        { withCredentials: true }
      );

      if (response.data.success) {
        const count = response.data.recordings_count || 0;
        if (count > 0) {
          modal.success(`Successfully fetched ${count} recording(s)!`);
        } else {
          modal.info('No recordings available for this meeting.');
        }
        fetchRecordings(); // Refresh the list
      } else {
        modal.error(response.data.message || response.data.error || 'Failed to fetch recordings');
      }
    } catch (err) {
      console.error('Error fetching recordings:', err);

      // Check if it's a rate limit error (429)
      if (err.response?.status === 429 && err.response?.data?.rate_limited) {
        const timeRemaining = err.response.data.time_remaining_seconds;
        const minutes = Math.floor(timeRemaining / 60);
        const seconds = timeRemaining % 60;

        let timeMsg = '';
        if (minutes > 0) {
          timeMsg = `${minutes} minute${minutes !== 1 ? 's' : ''}`;
          if (seconds > 0) {
            timeMsg += ` and ${seconds} second${seconds !== 1 ? 's' : ''}`;
          }
        } else {
          timeMsg = `${seconds} second${seconds !== 1 ? 's' : ''}`;
        }

        modal.warning(`Please wait ${timeMsg} before trying to fetch again.`);
      } else {
        modal.error(err.response?.data?.error || 'Failed to fetch recordings from SquadCast');
      }
    } finally {
      setFetchingRecordings(prev => ({ ...prev, [meetingId]: false }));
    }
  };

  const handleTranscribe = async (meetingId) => {
    // Check if this is a regeneration (for super admins)
    const recording = recordings.find(r => r.meeting_id === meetingId);
    if (isSuperAdmin && recording?.transcript_status === 'completed') {
      const result = await modal.confirm('This recording already has a transcript.\nAre you sure you want to regenerate it? This will overwrite the existing transcript.', 'Regenerate Transcript');
      if (!result) {
        return;
      }
    }

    setTranscribingRecordings(prev => ({ ...prev, [meetingId]: true }));

    try {
      const response = await axios.post(
        `/api/meetings/${meetingId}/transcribe`,
        {},
        { withCredentials: true }
      );

      if (response.data.success) {
        modal.success('Transcription started! This may take a few minutes. Refresh to check status.');
        fetchRecordings(); // Refresh the list
      } else {
        modal.error(response.data.error || 'Failed to start transcription');
      }
    } catch (err) {
      console.error('Error starting transcription:', err);
      modal.error(err.response?.data?.error || 'Failed to start transcription');
    } finally {
      setTranscribingRecordings(prev => ({ ...prev, [meetingId]: false }));
    }
  };

  const canTranscribe = (recording) => {
    // Can transcribe if: has recordings with URLs
    const hasRecordings = recording.recording_count > 0;

    // Super admins can always transcribe (forcefully regenerate)
    if (isSuperAdmin) {
      return hasRecordings;
    }

    // Regular admins/operators: can transcribe if transcript is not completed or processing
    const transcriptNotDone = !recording.transcript_status ||
                              recording.transcript_status === 'pending' ||
                              recording.transcript_status === 'failed';
    return hasRecordings && transcriptNotDone;
  };

  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    // Display times in user's local timezone with timezone abbreviation
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    });
  };

  const getStatusBadgeClass = (status, recording) => {
    // Determine badge color based on the 6-stage system

    // Stage 5: Ideas generated - Green
    if (recording.has_ideas_generated) {
      return 'status-badge-success';
    }

    // Stage 4: Transcribed, ready for ideas - Blue
    if (recording.transcript_status === 'completed' && recording.transcript) {
      return 'status-badge-info';
    }

    // Stage 3: Audio available, ready for transcription - Warning Yellow
    if (recording.recording_count > 0 && (!recording.transcript_status || recording.transcript_status === 'pending')) {
      return 'status-badge-warning';
    }

    // Fallback to status code
    switch(status) {
      case 'completed':
        return 'status-badge-success';
      case 'processing':
        return 'status-badge-warning';
      case 'pending':
        return 'status-badge-secondary';
      case 'no_recording':
        return 'status-badge-none';
      case 'failed':
        return 'status-badge-danger';
      // Legacy status codes
      case 'ideas_generated':
        return 'status-badge-success';
      case 'transcribed':
        return 'status-badge-info';
      case 'waiting_for_audio':
        return 'status-badge-warning';
      case 'not_started':
        return 'status-badge-secondary';
      default:
        return 'status-badge-secondary';
    }
  };

  const getStatusDisplayText = (status, recording) => {
    // 6-stage status system:
    // 1. Meeting created, not yet ended
    // 2. Meeting ended, polling for audio
    // 3. Audio available, ready for transcription
    // 4. Transcribed, ready for idea generation
    // 5. Ideas generated from this meeting
    // 6. Meeting ended, no recordings available

    // Stage 5: Check if ideas have been generated (highest priority)
    if (recording.has_ideas_generated) {
      return 'Ideas Generated';
    }

    // Stage 4: Check if transcribed and ready for ideas
    if (recording.transcript_status === 'completed' && recording.transcript) {
      return 'Ready For Idea Generation';
    }

    // Stage 3: Check if audio is available and ready for transcription
    if (recording.recording_count > 0 && (!recording.transcript_status || recording.transcript_status === 'pending')) {
      return 'Ready For Transcription';
    }

    // Now check the recording_status field
    switch(status) {
      case 'pending':
        // Stage 1: Meeting hasn't ended yet
        return 'Meeting Has Not Yet Concluded';
      case 'processing':
        // Stage 2: Meeting ended, polling for audio
        return 'Waiting For Audio';
      case 'completed':
        // This should be caught by the checks above, fallback to ideas generated
        return 'Ideas Generated';
      case 'no_recording':
        // Stage 6: No recordings available
        return 'No Recordings';
      case 'failed':
        return 'Error';
      // Legacy status codes from previous system (fallback handling)
      case 'not_started':
        return 'Meeting Has Not Yet Concluded';
      case 'waiting_for_audio':
        return 'Waiting For Audio';
      case 'transcribed':
        return 'Ready For Idea Generation';
      case 'ideas_generated':
        return 'Ideas Generated';
      default:
        return status || 'Unknown';
    }
  };

  const canFetchRecordings = (recording) => {
    // Super admins can always refetch (to update recordings)
    if (isSuperAdmin) {
      return true;
    }

    // Regular admins/operators: can fetch if recordings haven't been successfully retrieved yet
    // Note: 'no_recording' is allowed - operators can retry if SquadCast was slow to process
    const status = recording.recording_status;
    return status !== 'completed' &&
           status !== 'ideas_generated' &&
           status !== 'transcribed';
  };

  const getTranscriptStatusBadgeClass = (status) => {
    switch(status) {
      case 'completed':
        return 'status-badge-success';
      case 'processing':
        return 'status-badge-warning';
      case 'failed':
        return 'status-badge-danger';
      default:
        return 'status-badge-secondary';
    }
  };

  const getTranscriptStatusText = (status) => {
    switch(status) {
      case 'completed':
        return 'Ready';
      case 'processing':
        return 'Processing';
      case 'failed':
        return 'Failed';
      case 'pending':
        return 'Pending';
      default:
        return '-';
    }
  };

  const openTranscript = (recording) => {
    setTranscriptModal({
      open: true,
      transcript: recording.transcript,
      title: recording.title || 'Meeting Transcript'
    });
  };

  const closeTranscriptModal = () => {
    setTranscriptModal({ open: false, transcript: null, title: '' });
  };

  const copyTranscript = async () => {
    if (transcriptModal.transcript) {
      try {
        await navigator.clipboard.writeText(transcriptModal.transcript);
        modal.toast('Transcript copied to clipboard', 'success');
      } catch (err) {
        console.error('Failed to copy transcript:', err);
      }
    }
  };

  const handleGenerateIdeas = async (meetingId) => {
    const recording = recordings.find(r => r.meeting_id === meetingId);
    if (isSuperAdmin && recording?.has_ideas_generated) {
      const result = await modal.confirm('This meeting already has ideas generated.\nGenerate another batch? This adds a new set of ideas alongside the existing ones - it does not overwrite them.', 'Regenerate Ideas');
      if (!result) {
        return;
      }
    } else {
      const result = await modal.confirm('Generate AI-powered content ideas from this meeting transcript?', 'Generate Ideas');
      if (!result) {
        return;
      }
    }

    setGeneratingIdeas(prev => ({ ...prev, [meetingId]: true }));

    try {
      const response = await axios.post('/api/ideas/generate', {
        meeting_id: meetingId
      }, { withCredentials: true });

      if (response.data.success) {
        if (response.data.status === 'partial_success') {
          modal.success(
            `${response.data.reason || `Generated ${response.data.ideas_count} quality ideas from the transcript.`} Go to Ideas > Review Ideas to review them.`
          );
        } else {
          modal.success(`Successfully generated ${response.data.ideas_count} ideas! Go to Ideas > Review Ideas to review them.`);
        }
      } else {
        modal.error(response.data.error || 'Failed to generate ideas');
      }
    } catch (err) {
      console.error('Error generating ideas:', err);
      const errorData = err.response?.data;

      // Handle specific error cases with helpful messages
      if (errorData?.missing_assignment) {
        modal.error(`Cannot generate ideas: This recording needs to be assigned to an organization and brand first.\n\nUse the "Assign" dropdown in the Organization or Brand column to assign this recording.`);
      } else if (errorData?.empty_brand_document) {
        modal.error(`Cannot generate ideas: The brand profile for "${errorData.brand_name}" is empty.\n\nPlease go to Brand Identity and fill in the brand profile with information about the brand's audience, messaging, and content pillars.`);
      } else {
        modal.error(errorData?.error || 'Failed to generate ideas.\nMake sure the meeting has a transcript.');
      }
    } finally {
      setGeneratingIdeas(prev => ({ ...prev, [meetingId]: false }));
    }
  };

  const canGenerateIdeas = (recording) => {
    // Can generate ideas if:
    // 1. Transcript is completed
    // 2. Recording has organization assigned
    // 3. Recording has brand assigned
    // 4. Ideas have NOT already been generated from this meeting
    //    (Super admins can always regenerate, e.g. to test prompt/logic changes
    //    without re-recording a meeting)
    const hasTranscript = recording.transcript_status === 'completed' && recording.transcript;
    const hasOrg = recording.organization?.name || recording.organization_id;
    const hasBrand = recording.brand?.name || recording.brand_id;

    if (isSuperAdmin) {
      return hasTranscript && hasOrg && hasBrand;
    }

    const ideasNotGenerated = !recording.has_ideas_generated;
    return hasTranscript && hasOrg && hasBrand && ideasNotGenerated;
  };

  const getGenerateIdeasTooltip = (recording) => {
    const hasTranscript = recording.transcript_status === 'completed' && recording.transcript;
    const hasOrg = recording.organization?.name || recording.organization_id;
    const hasBrand = recording.brand?.name || recording.brand_id;

    if (recording.has_ideas_generated) {
      return isSuperAdmin
        ? "Ideas already generated - click to generate another batch (Super Admin)"
        : "Ideas already generated from this meeting";
    }
    if (!hasTranscript) {
      return "Transcript required to generate ideas";
    }
    if (!hasOrg || !hasBrand) {
      const missing = [];
      if (!hasOrg) missing.push("organization");
      if (!hasBrand) missing.push("brand");
      return `Assign ${missing.join(" and ")} first`;
    }
    return "Generate AI content ideas";
  };

  // Check if a recording needs assignment
  const needsAssignment = (recording) => {
    const hasOrg = recording.organization?.name || recording.organization_id;
    const hasBrand = recording.brand?.name || recording.brand_id;
    return !hasOrg || !hasBrand;
  };

  // Handle assigning a recording to an org/brand
  const handleAssignRecording = async (meetingId, organizationId, brandId) => {
    setAssigningRecording(prev => ({ ...prev, [meetingId]: true }));

    try {
      const response = await axios.put(`/api/meetings/${meetingId}/assign`, {
        organization_id: organizationId || undefined,
        brand_id: brandId || undefined
      }, { withCredentials: true });

      if (response.data.success) {
        // Refresh recordings to show updated assignment
        fetchRecordings();
      } else {
        modal.error(response.data.error || 'Failed to assign recording');
      }
    } catch (err) {
      console.error('Error assigning recording:', err);
      modal.error(err.response?.data?.error || 'Failed to assign recording');
    } finally {
      setAssigningRecording(prev => ({ ...prev, [meetingId]: false }));
    }
  };

  // Get brands for a specific organization
  const getBrandsForOrg = (orgId) => {
    if (!orgId) return accessibleBrands;
    return accessibleBrands.filter(b =>
      b.organization_id === orgId ||
      String(b.organization_id) === String(orgId)
    );
  };

  const getAvailableFormats = (audioFile) => {
    if (audioFile.formats && Object.keys(audioFile.formats).length > 0) {
      // Filter out webm, wav, and mp4 formats (not supported/too large)
      // Only allow mp3 downloads
      const filteredFormats = {};
      for (const [key, value] of Object.entries(audioFile.formats)) {
        if (key === 'mp3') {
          filteredFormats[key] = value;
        }
      }
      return filteredFormats;
    }
    // Fallback for old recordings that only have url
    if (audioFile.url) {
      return { 'download': audioFile.url };
    }
    return {};
  };

  const handleFormatChange = (recordingId, fileIndex, format) => {
    const key = `${recordingId}-${fileIndex}`;
    setSelectedFormats(prev => ({ ...prev, [key]: format }));
  };

  const getSelectedFormat = (recordingId, fileIndex, formats) => {
    const key = `${recordingId}-${fileIndex}`;
    const formatKeys = Object.keys(formats);
    return selectedFormats[key] || formatKeys[0];
  };

  const renderEmptyState = () => {
    return (
      <div className="empty-state-recordings">
        <div className="empty-icon"><Icon name="play" size="3rem" /></div>
        <h3>No Recordings Found</h3>
        <p>There are no recordings for this agency yet.</p>
        <p className="hint-text">
          Recordings will appear here after meetings with SquadCast are completed.
        </p>
      </div>
    );
  };


  const renderOrgDropdown = (recording) => (
    <div className="assign-dropdown-container">
        <CustomSelect
          className="assign-select"
          defaultValue={recording.organization?.id || ''}
          onChange={(e) => {
            if (e.target.value) {
              handleAssignRecording(recording.meeting_id, e.target.value, null);
              setEditingOrg(prev => ({ ...prev, [recording.meeting_id]: false }));
            }
          }}
          disabled={assigningRecording[recording.meeting_id]}
        >
          <option value="">Assign Org...</option>
          {accessibleOrgs.map(org => (
            <option key={org._id} value={org._id}>
              {org.organization_name || org.name}
            </option>
          ))}
        </CustomSelect>
      {assigningRecording[recording.meeting_id] && (
        <span className="spinner-small assign-spinner"></span>
      )}
      {recording.organization?.name && (
        <button className="cancel-edit-btn" onClick={() => setEditingOrg(prev => ({ ...prev, [recording.meeting_id]: false }))}>
          <Icon name="close-x" size="0.75rem" />
        </button>
      )}
    </div>
  );

  const getOrganizationDisplay = (recording) => {
    // If editing mode is on for this recording, show dropdown
    if (editingOrg[recording.meeting_id]) {
      return renderOrgDropdown(recording);
    }

    if (recording.organization?.icon_url) {
      return (
        <>
          <BrandIcon src={recording.organization.icon_url} alt={recording.organization.name} className="org-logo" />
          <span className="org-name">{recording.organization.name}</span>
          {(isSuperAdmin || isAdmin) && accessibleOrgs.length > 0 && (
            <button className="edit-assign-btn" title="Change organization" onClick={() => setEditingOrg(prev => ({ ...prev, [recording.meeting_id]: true }))}>
              <Icon name="pencil" size="0.85rem" />
            </button>
          )}
        </>
      );
    }
    if (recording.organization?.name) {
      return (
        <>
          <div className="org-placeholder">
            <span>{recording.organization.name.charAt(0).toUpperCase()}</span>
          </div>
          <span className="org-name">{recording.organization.name}</span>
          {(isSuperAdmin || isAdmin) && accessibleOrgs.length > 0 && (
            <button className="edit-assign-btn" title="Change organization" onClick={() => setEditingOrg(prev => ({ ...prev, [recording.meeting_id]: true }))}>
              <Icon name="pencil" size="0.85rem" />
            </button>
          )}
        </>
      );
    }

    // No organization - show assignment dropdown
    if (accessibleOrgs.length > 0) {
      return renderOrgDropdown(recording);
    }

    return (
      <>
        <div className="org-placeholder">
          <Icon name="feeds" size="1.5rem" />
        </div>
        <span className="missing-data">No Organization</span>
      </>
    );
  };

  const renderBrandDropdown = (recording) => {
    const orgId = recording.organization_id || recording.organization?.id;
    const availableBrands = orgId ? getBrandsForOrg(orgId) : accessibleBrands;

    return (
      <div className="assign-dropdown-container">
        <CustomSelect
          className="assign-select"
          defaultValue={recording.brand?.id || ''}
          onChange={(e) => {
            if (e.target.value) {
              handleAssignRecording(recording.meeting_id, null, e.target.value);
              setEditingBrand(prev => ({ ...prev, [recording.meeting_id]: false }));
            }
          }}
          disabled={assigningRecording[recording.meeting_id]}
        >
          <option value="">Assign Brand...</option>
          {availableBrands.map(brand => (
            <option key={brand._id} value={brand._id}>
              {brand.brand_name || brand.name}
            </option>
          ))}
        </CustomSelect>
        {assigningRecording[recording.meeting_id] && (
          <span className="spinner-small assign-spinner"></span>
        )}
        {recording.brand?.name && (
          <button className="cancel-edit-btn" onClick={() => setEditingBrand(prev => ({ ...prev, [recording.meeting_id]: false }))}>
            <Icon name="close-x" size="0.75rem" />
          </button>
        )}
      </div>
    );
  };

  const getBrandDisplay = (recording) => {
    const orgId = recording.organization_id || recording.organization?.id;
    const availableBrands = orgId ? getBrandsForOrg(orgId) : accessibleBrands;

    // If editing mode is on for this recording, show dropdown
    if (editingBrand[recording.meeting_id]) {
      return renderBrandDropdown(recording);
    }

    if (recording.brand?.icon_url) {
      return (
        <>
          <BrandIcon src={recording.brand.icon_url} alt={recording.brand.name} className="org-logo" />
          <span className="org-name">{recording.brand.name}</span>
          {(isSuperAdmin || isAdmin) && availableBrands.length > 0 && (
            <button className="edit-assign-btn" title="Change brand" onClick={() => setEditingBrand(prev => ({ ...prev, [recording.meeting_id]: true }))}>
              <Icon name="pencil" size="0.85rem" />
            </button>
          )}
        </>
      );
    }
    if (recording.brand?.name) {
      return (
        <>
          <div className="org-placeholder">
            <span>{recording.brand.name.charAt(0).toUpperCase()}</span>
          </div>
          <span className="org-name">{recording.brand.name}</span>
          {(isSuperAdmin || isAdmin) && availableBrands.length > 0 && (
            <button className="edit-assign-btn" title="Change brand" onClick={() => setEditingBrand(prev => ({ ...prev, [recording.meeting_id]: true }))}>
              <Icon name="pencil" size="0.85rem" />
            </button>
          )}
        </>
      );
    }

    // No brand - show assignment dropdown
    if (availableBrands.length > 0) {
      return renderBrandDropdown(recording);
    }

    return (
      <>
        <div className="org-placeholder">
          <Icon name="feeds" size="1.5rem" />
        </div>
        <span className="missing-data">No Brand</span>
      </>
    );
  };

  const sortableRecordings = recordings.map(r => ({
    ...r,
    _sortOrg: r.organization?.name || '',
    _sortBrand: r.brand?.name || '',
  }));
  const { sortedData: sortedRecordings, requestSort: requestRecSort, getSortClass: getRecSortClass } = useSortableTable(sortableRecordings);

  if (loading) {
    return (
      <div className="super-admin-internal-layout">
        <nav className="internal-nav">
          <div className="internal-nav-items">
            <button className="internal-nav-btn active">
              Recordings
            </button>
          </div>
        </nav>
        <div className="internal-content">
          <div className="recordings-container">
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading recordings...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="super-admin-internal-layout">
      <nav className="internal-nav">
        <div className="internal-nav-items">
          <button className="internal-nav-btn active">
            Recordings
          </button>
        </div>
      </nav>
      <div className="internal-content">
      <div className="recordings-container">
      <div className="recordings-content-wrapper">

        {recordings.length === 0 ? (
          renderEmptyState()
        ) : (
          <>
            {isPolling && (
              <div className="recordings-header-controls">
                <div className="recordings-list-header">
                  <span className="polling-indicator" title="Auto-refreshing for pending recordings">
                    <span className="polling-dot"></span>
                    Auto-updating
                  </span>
                </div>
              </div>
            )}
            {/* Filter/Refresh controls hidden for now
              <div className="header-actions">
                  <CustomSelect
                    className="filter-status"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <option value="all">All Statuses</option>
                    <option value="completed">Has Recordings / Ideas</option>
                    <option value="processing">Waiting For Audio</option>
                    <option value="pending">Meeting Not Yet Concluded</option>
                    <option value="no_recording">No Recordings</option>
                    <option value="failed">Error</option>
                  </CustomSelect>
                <button className="btn-refresh" onClick={handleRefresh}>
                  Refresh
                </button>
              </div>
              */}

            <div className="recordings-table-card">
              <table className="recordings-table">
                <thead>
                  <tr>
                    <th className={getRecSortClass('_sortOrg')} onClick={() => requestRecSort('_sortOrg')}>Organization</th>
                    <th className={getRecSortClass('_sortBrand')} onClick={() => requestRecSort('_sortBrand')}>Brand</th>
                    <th className={getRecSortClass('title')} onClick={() => requestRecSort('title')}>Meeting Title</th>
                    <th className={getRecSortClass('scheduled_start')} onClick={() => requestRecSort('scheduled_start')}>Scheduled Date</th>
                    <th className={getRecSortClass('recording_status')} onClick={() => requestRecSort('recording_status')}>Status</th>
                    <th>Download</th>
                    <th>Transcript</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRecordings.map((recording) => (
                    <tr key={recording.meeting_id}>
                      <td className="org-cell">
                        <div className="org-cell-content">
                          {getOrganizationDisplay(recording)}
                        </div>
                      </td>
                      <td className="org-cell">
                        <div className="org-cell-content">
                          {getBrandDisplay(recording)}
                        </div>
                      </td>
                      <td className="title-cell">
                        <strong>{recording.title || 'Untitled Meeting'}</strong>
                      </td>
                      <td className="date-cell">
                        {formatDateTime(recording.scheduled_start)}
                      </td>
                      <td className="status-cell">
                        <div className="status-cell-content">
                          <div className={`status-badge ${getStatusBadgeClass(recording.recording_status, recording)}`}>
                            {getStatusDisplayText(recording.recording_status || 'pending', recording)}
                          </div>
                          {recording.has_ai_analysis && (
                            <span className="ai-badge" title="AI Transcription Available"><Icon name="ai" size="1rem" /></span>
                          )}
                        </div>
                      </td>
                      <td className="audio-cell">
                        {recording.audio_files && recording.audio_files.length > 0 ? (
                          <div className="audio-files">
                            {recording.audio_files.map((file, index) => {
                              const formats = getAvailableFormats(file);
                              const formatKeys = Object.keys(formats);
                              const selectedFormat = getSelectedFormat(recording.meeting_id, index, formats);

                              return (
                                <div key={index} className="audio-file-row">
                                  <span className="audio-name">{file.name || `Recording ${index + 1}`}</span>
                                  <div className="audio-download">
                                    {formatKeys.length > 1 ? (
                                      <>
                                          <CustomSelect
                                            className="format-select"
                                            value={selectedFormat}
                                            onChange={(e) => handleFormatChange(recording.meeting_id, index, e.target.value)}
                                          >
                                            {formatKeys.map(fmt => (
                                              <option key={fmt} value={fmt}>{fmt.toUpperCase()}</option>
                                            ))}
                                          </CustomSelect>
                                        <a
                                          href={formats[selectedFormat]}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="download-btn"
                                          title="Download"
                                        >
                                          <Icon name="down-open" size="1.2rem" />
                                        </a>
                                      </>
                                    ) : formatKeys.length === 1 ? (
                                      <a
                                        href={formats[formatKeys[0]]}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="download-btn-single"
                                        title={`Download ${formatKeys[0].toUpperCase()}`}
                                      >
                                        <Icon name="down-open" size="1rem" /> {formatKeys[0].toUpperCase()}
                                      </a>
                                    ) : null}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <span className="no-audio">-</span>
                        )}
                      </td>
                      <td className="transcript-cell">
                        {recording.transcript_status ? (
                          <div className="transcript-status">
                            {recording.transcript_status === 'completed' && recording.transcript ? (
                              <span
                                className={`status-badge status-badge-success clickable`}
                                onClick={() => openTranscript(recording)}
                                title="Click to view transcript"
                              >
                                Ready - View
                              </span>
                            ) : (
                              <span className={`status-badge ${getTranscriptStatusBadgeClass(recording.transcript_status)}`}>
                                {getTranscriptStatusText(recording.transcript_status)}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="no-transcript">-</span>
                        )}
                      </td>
                      <td className="sq-actions-cell">
                        <div className="sq-actions-buttons">
                          <button
                            className="sq-action-btn sq-fetch"
                            onClick={() => handleFetchRecordings(recording.meeting_id)}
                            title={
                              !canFetchRecordings(recording)
                                ? "Recordings already fetched or unavailable"
                                : (isSuperAdmin && recording.recording_status === 'completed')
                                  ? "Refetch recordings from SquadCast (Super Admin)"
                                  : "Fetch recordings from SquadCast"
                            }
                            disabled={fetchingRecordings[recording.meeting_id] || !canFetchRecordings(recording)}
                          >
                            {fetchingRecordings[recording.meeting_id] ? (
                              <span className="spinner-small"></span>
                            ) : (
                              <Icon name="forward" size="1rem" />
                            )}
                          </button>
                          <button
                            className="sq-action-btn sq-transcribe"
                            onClick={() => handleTranscribe(recording.meeting_id)}
                            title={
                              !canTranscribe(recording)
                                ? "No recordings to transcribe"
                                : (isSuperAdmin && recording.transcript_status === 'completed')
                                  ? "Regenerate transcript (Super Admin)"
                                  : "Transcribe recording"
                            }
                            disabled={transcribingRecordings[recording.meeting_id] || !canTranscribe(recording)}
                          >
                            {transcribingRecordings[recording.meeting_id] ? (
                              <span className="spinner-small"></span>
                            ) : (
                              <Icon name="doc" size="1rem" />
                            )}
                          </button>
                          <button
                            className="sq-action-btn sq-generate-ideas"
                            onClick={() => handleGenerateIdeas(recording.meeting_id)}
                            title={getGenerateIdeasTooltip(recording)}
                            disabled={generatingIdeas[recording.meeting_id] || !canGenerateIdeas(recording)}
                          >
                            {generatingIdeas[recording.meeting_id] ? (
                              <span className="spinner-small"></span>
                            ) : (
                              <Icon name="lightbulb" size="1rem" />
                            )}
                          </button>
                          <button
                            className="sq-action-btn sq-delete"
                            onClick={() => handleDelete(recording.meeting_id)}
                            title="Delete recording"
                          >
                            <Icon name="close-x" size="1rem" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Transcript Modal */}
      {transcriptModal.open && (
        <div className="transcript-modal-overlay" onClick={closeTranscriptModal}>
          <div className="transcript-modal" onClick={(e) => e.stopPropagation()}>
            <div className="transcript-modal-header">
              <h3>{transcriptModal.title}</h3>
              <div className="transcript-modal-actions">
                {transcriptModal.transcript && (
                  <button
                    type="button"
                    className="copy-transcript-btn"
                    onClick={copyTranscript}
                    title="Copy transcript to clipboard"
                    style={{
                      padding: '0.25rem 0.5rem',
                      fontSize: '0.8rem',
                      background: '#F3F4F6',
                      color: '#111827',
                      border: '1px solid #D1D5DB',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}
                  >
                    <Icon name="docs" size="0.875rem" />
                    Copy
                  </button>
                )}
                <button className="modal-close-btn" onClick={closeTranscriptModal} aria-label="Close" />
              </div>
            </div>
            <div className="transcript-modal-content">
              {transcriptModal.transcript ? (
                <pre className="transcript-text">{transcriptModal.transcript}</pre>
              ) : (
                <p className="no-transcript-message">No transcript available.</p>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
      </div>
    </div>
  );
};

export default Recordings;
